<template lang="pug">
.participants-block
  label.input-label Number of participants
  .participants-grid
    template(v-for="segment in numberOfPersonsPerSegment")
      BriqNumberInputWithButtons(
        :data-test-id="`total-quantity-input-${segment.SegmentCode}`"
        type="number"
        step="1"
        min="0"
        :key="segment.SegmentCode"
        v-model.number="segment.NumberOfPersons"
        @input="maybeSyncProductQuantities(segment.SegmentCode)"
        )
      span.segment-label {{ segment.SegmentNamePlural }}
  hr.sum-line
  .participants-grid
    span.bold.text-center(data-test-id="participants-total") {{ totalNumberOfPersons }}
    span.bold Total
</template>

<script>
import BriqNumberInputWithButtons from '@v/components/BriqNumberInputWithButtons.vue'
import {
  pipe,
  filter,
  prop,
  map,
  intersection,
  path,
  find,
  propEq,
  reduce,
  all,
  includes,
  __,
  any,
  append,
} from 'ramda'
import { mapMutations } from 'vuex'
import { sumProp, maybeMutate, makeObj } from '@v/util/briqRamdaExt.js'
import {
  pathToNumberOfPersonsPerSegment,
  pathToQuantitiesPerSegment,
  getTotalNumberOfPersons,
} from '@v/bookings/edit/util/segmentUtil.js'
import filterAndSortBookingSegments from '@v/bookings/edit/util/filterAndSortBookingSegments.js'

export default {
  name: 'NumberOfPersonsPerSegment',
  components: { BriqNumberInputWithButtons },
  props: {
    booking: { type: Object, required: true },
    classifications: { type: Array, required: true },
  },
  computed: {
    isNewBooking() {
      return !this.booking.Key
    },
    totalNumberOfPersons() {
      return getTotalNumberOfPersons(this.booking)
    },
    numberOfPersonsPerSegment() {
      return numberOfPersonsPerSegment({
        booking: this.booking,
        classifications: this.classifications,
      })
    },
  },
  methods: {
    ...mapMutations('bookingUpsert', ['incrementScheduledProductsIncHelper']),
    maybeSyncProductQuantities(segmentCode) {
      const { isNewBooking, booking, $bus } = this
      if (!isNewBooking) return false
      const totalNumberOfPersons = getTotalNumberOfPersons(booking)
      const numberOfPersonsInSegment = getNumberOfPersonsInSegment(segmentCode)(booking)
      let includesDeal = false
      booking.BookingProducts.forEach(bookingProduct => {
        if (bookingProduct.ProductGroupKey) {
          includesDeal = true
          this.incrementScheduledProductsIncHelper()
          updateDealProductQuantity({
            booking,
            bookingProduct,
            totalNumberOfPersons,
            segmentCode,
            $bus,
            numberOfPersonsInSegment,
          })
        } else
          updateProductQuantity({
            bookingProduct,
            segmentCode,
            segmentlessQuantity: totalNumberOfPersons,
            quantityInSegment: numberOfPersonsInSegment,
          })
      })
      if (includesDeal) this.$store.commit('bookingUpsert/updateBookingDeals')
    },
  },
}

// === Utility functions ===

const getNumberOfPersonsInSegment = segmentCode =>
  pipe(
    pathToNumberOfPersonsPerSegment,
    find(propEq('SegmentCode', segmentCode)),
    prop('NumberOfPersons'),
  )

const setQuantityInSegment = (segmentCode, quantity) =>
  pipe(
    pathToQuantitiesPerSegment,
    find(propEq('SegmentCode', segmentCode)),
    maybeMutate('Quantity', quantity),
  )

function updateProductQuantity({
  bookingProduct,
  segmentCode,
  segmentlessQuantity,
  quantityInSegment,
}) {
  if (bookingProduct.isSegmentless) bookingProduct.ProductQuantity = segmentlessQuantity
  else setQuantityInSegment(segmentCode, quantityInSegment)(bookingProduct)
}

// are there multiple BookingProducts that are coupled to the same deal product?
const bookingHasIdenticalDealProducts = productGroupProductKey =>
  pipe(
    prop('BookingProducts'),
    reduce((a, n) => (n.ProductGroupProductKey === productGroupProductKey ? a + 1 : a), 0),
    x => x > 1,
  )

const segmentIntersection = pipe(
  filter(x => !x.isSegmentless),
  map(pipe(pathToQuantitiesPerSegment, map(prop('SegmentCode')))),
  x => reduce(intersection, x[0], x),
)

const getBookingProductsFromDeal = productGroupKey =>
  pipe(prop('BookingProducts'), filter(propEq('ProductGroupKey', productGroupKey)))

const getIntersectedSegments = dealKey =>
  pipe(
    getBookingProductsFromDeal(dealKey),
    makeObj({
      intersectedSegments: segmentIntersection,
      dealIsSegmentless: all(p => p.isSegmentless),
    }),
  )

function updateDealProductQuantity({
  booking,
  bookingProduct,
  totalNumberOfPersons,
  segmentCode,
  numberOfPersonsInSegment,
}) {
  const dealKey = bookingProduct.ProductGroupKey
  const { intersectedSegments, dealIsSegmentless } = getIntersectedSegments(dealKey)(booking)
  const segmentExistsOnThisDeal = includes(__, intersectedSegments)
  const sumPersonsForDealSegments = pipe(
    pathToNumberOfPersonsPerSegment,
    filter(pipe(prop('SegmentCode'), segmentExistsOnThisDeal)),
    sumProp('NumberOfPersons'),
  )

  const totalOfDealSegments = dealIsSegmentless
    ? totalNumberOfPersons
    : sumPersonsForDealSegments(booking)

  if (dealIsSegmentless || segmentExistsOnThisDeal(segmentCode)) {
    const hasIdenticalDealProducts = bookingHasIdenticalDealProducts(
      bookingProduct.ProductGroupProductKey,
    )(booking)
    const newSegmentQuantity = numberOfPersonsInSegment * bookingProduct.QuantityInProductGroup
    if (!hasIdenticalDealProducts)
      updateProductQuantity({
        bookingProduct,
        segmentCode,
        segmentlessQuantity: totalOfDealSegments * bookingProduct.QuantityInProductGroup,
        quantityInSegment: newSegmentQuantity,
      })
  }
}

// enabled segments that are not on booking are complemented here
function numberOfPersonsPerSegment({ booking, classifications }) {
  const segmentsOnBooking = pathToNumberOfPersonsPerSegment(booking)
  const segments = path([0, 'Segments'], classifications)
  return filterAndSortBookingSegments(segmentsOnBooking, segments)
}
</script>

<style scoped lang="stylus">
.participants-block
  margin-bottom 24px

.participants-grid
  display grid
  grid-template-columns 160px auto
  gap 16px

.segment-people
  margin-bottom 13px

.sum-line
  margin-top 14px
  margin-bottom 12px

.segment-label
  display flex
  align-items center
</style>

<style lang="stylus">
.participants-grid
  input.input, .text-center
    text-align center
</style>
